import { useCallback, useMemo } from 'react';

import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

import type { UnconnectedApp } from '../../app-connection-banner';

import { getKnowledgeSourceDisplayName } from './utils/get-knowledge-source-display-name';

/**
 * Hook that returns a function to get unconnected apps for a given list of app IDs.
 * Used to determine if a prompt requires apps that the user hasn't connected to yet.
 */
export function useUnconnectedAppsForPrompt(
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'],
): {
	getUnconnectedApps: (appIds: string[]) => UnconnectedApp[];
} {
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const [storeAllSources] = useEnabledKnowledgeSources();

	const allSources = useMemo(() => storeAllSources, [storeAllSources]);

	const getUnconnectedApps = useCallback(
		(appIds: string[]): UnconnectedApp[] => {
			if (!appIds || appIds.length === 0) {
				return [];
			}

			return allSources
				.filter((source) => appIds.includes(source.source) && !source.isUserOAuthed)
				.map((source) => ({
					source: source.source,
					friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
					iconUrl: source.iconUrl,
					outboundAuthUrl: source.outboundAuthUrl,
				}));
		},
		[allSources],
	);

	return { getUnconnectedApps };
}
